import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import './ScrollSpyTabs.css';
/**
 * Klebende Abschnitts-Navigation: markiert den sichtbaren Abschnitt und scrollt beim Klick dorthin.
 * Die Marke unter dem aktiven Tab entspricht der 12-Uhr-Markierung einer Lünette.
 */
const ScrollSpyTabs = ({ sections, scrollOffset = 120, variant = 'default', children }) => {
    const [activeSection, setActiveSection] = useState(sections[0]?.id || '');
    const sectionsRef = useRef({});
    const navRef = useRef(null);
    const barRef = useRef(null);
    // Abstand oben = Unterkante der klebenden Leiste (auf dem Handy mehrzeilig, daher dynamisch)
    const offset = useCallback(() => {
        const bar = barRef.current;
        return bar ? Math.max(scrollOffset, bar.getBoundingClientRect().height + 64 + 12) : scrollOffset;
    }, [scrollOffset]);
    const [overflow, setOverflow] = useState({ left: false, right: false });
    // Zeigt an, ob die Leiste seitlich weiter scrollt (Verlauf + Pfeil am Rand)
    useEffect(() => {
        const nav = navRef.current;
        if (!nav)
            return;
        const update = () => setOverflow({
            left: nav.scrollLeft > 4,
            right: nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 4,
        });
        update();
        nav.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        return () => {
            nav.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [sections]);
    const nudge = (dir) => {
        const nav = navRef.current;
        if (nav)
            nav.scrollBy({ left: dir * nav.clientWidth * 0.7, behavior: 'smooth' });
    };
    useEffect(() => {
        const handleScroll = () => {
            let current = sections[0]?.id || '';
            for (const s of sections) {
                const el = sectionsRef.current[s.id];
                if (!el)
                    continue;
                if (el.getBoundingClientRect().top - offset() - 8 <= 0)
                    current = s.id;
            }
            // Am Seitenende den letzten Abschnitt markieren
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
                current = sections[sections.length - 1]?.id || current;
            }
            setActiveSection(current);
        };
        let ticking = false;
        const throttled = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener('scroll', throttled, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', throttled);
    }, [sections, offset]);
    // Aktiven Tab in der horizontal scrollbaren Leiste sichtbar halten
    useEffect(() => {
        const nav = navRef.current;
        const btn = nav?.querySelector(`[data-tab="${activeSection}"]`);
        if (nav && btn) {
            const left = btn.offsetLeft - nav.clientWidth / 2 + btn.clientWidth / 2;
            nav.scrollTo({ left, behavior: 'smooth' });
        }
    }, [activeSection]);
    const handleTabClick = useCallback((id) => {
        const el = sectionsRef.current[id];
        if (el) {
            const top = window.scrollY + el.getBoundingClientRect().top - offset();
            window.scrollTo({ top, behavior: 'smooth' });
            setActiveSection(id);
        }
    }, [offset]);
    const registerRef = useCallback((id) => (el) => {
        sectionsRef.current[id] = el;
    }, []);
    return (_jsxs("div", { className: `section-tabs section-tabs-${variant}`, children: [sections.length > 1 && (_jsxs("div", { ref: barRef, className: `section-tabs-bar ${overflow.left ? 'more-left' : ''} ${overflow.right ? 'more-right' : ''}`, children: [overflow.left && (_jsx("button", { type: "button", className: "section-tabs-more left", "aria-hidden": "true", tabIndex: -1, onClick: () => nudge(-1), children: "\u2039" })), overflow.right && (_jsx("button", { type: "button", className: "section-tabs-more right", "aria-hidden": "true", tabIndex: -1, onClick: () => nudge(1), children: "\u203A" })), _jsx("div", { className: "section-tabs-nav", ref: navRef, children: sections.map((s) => (_jsxs("button", { type: "button", "data-tab": s.id, className: `section-tab ${activeSection === s.id ? 'active' : ''}`, style: s.tone ? { '--tab-tone': `var(--tone-${s.tone})` } : undefined, "aria-current": activeSection === s.id ? 'true' : undefined, onClick: () => handleTabClick(s.id), children: [s.icon, _jsx("span", { children: s.label })] }, s.id))) })] })), _jsx("div", { className: "section-tabs-content", children: children(registerRef) })] }));
};
export default ScrollSpyTabs;
